// Minigame registry + shared helpers. Each game module defines:
//   {
//     id, label, stat, icon, description,
//     paramsFromStats(myStat, theirStat) -> {...},
//     Component({ challenge, mySide, params, seed, onScore }) -> JSX
//   }
//
// Adding a new game = create a new file under src/lib/minigames/ and import it
// here. Both players run the Component locally with the same seed; their
// per-side `params` come from `paramsFromStats(my, their)` — only the params
// differ between sides.

import highCard           from './highCard';
import whackAMole         from './whackAMole';
import rockPaperScissors  from './rockPaperScissors';
import simon              from './simon';
import tugOfWar           from './tugOfWar';
import drinkingContest    from './drinkingContest';
import spotDifference     from './spotDifference';

export const MINIGAMES = {
  [highCard.id]:          highCard,
  [whackAMole.id]:        whackAMole,
  [rockPaperScissors.id]: rockPaperScissors,
  [simon.id]:             simon,
  [tugOfWar.id]:          tugOfWar,
  [drinkingContest.id]:   drinkingContest,
  [spotDifference.id]:    spotDifference,
};

export const MINIGAME_LIST = Object.values(MINIGAMES);

export function getGame(id) {
  return MINIGAMES[id] || null;
}

// D&D 5e ability modifier from a raw score.
export function mod(score) {
  return Math.floor(((score ?? 10) - 10) / 2);
}

// Tiny LCG seeded PRNG — same seed produces the same sequence on both
// players' devices, which is how Whack-a-Mole and High Card stay in sync
// without exchanging frame-by-frame state.
export function seededRandom(seed) {
  let s = (seed | 0) || 1;
  return function next() {
    // Numerical recipes LCG constants
    s = (s * 1664525 + 1013904223) | 0;
    // Bring into [0, 1)
    return ((s >>> 0) % 0x100000000) / 0x100000000;
  };
}

// Pick a 32-bit-ish integer seed. Good enough for sub-15s minigames.
export function newSeed() {
  return Math.floor(Math.random() * 2 ** 31);
}

// Resolve a challenge outcome from both scores. Most games use a simple
// numeric > comparison; some (e.g. RPS, High Card) need to compare richer
// payloads. A game module can opt in by exporting
//   computeWinner(fromScore, toScore) → 'from' | 'to' | 'tie'
//
// On a true tie at the score level, the framework applies a STAT-BASED
// TIEBREAKER: the player with the higher relevant ability score wins.
// Returns { winner, tiebreaker } where tiebreaker is 'stat' if the result
// only resolved via stat advantage, else null. Lets the UI badge the
// "edged out on INT" case so players know what happened.
export function resolveOutcome(gameId, fromScore, toScore, fromStat, toStat) {
  const game = getGame(gameId);
  let raw;
  if (game?.computeWinner) {
    raw = game.computeWinner(fromScore, toScore);
  } else {
    const a = typeof fromScore === 'object' && fromScore !== null && 'value' in fromScore ? fromScore.value : fromScore;
    const b = typeof toScore   === 'object' && toScore   !== null && 'value' in toScore   ? toScore.value   : toScore;
    if (a > b) raw = 'from';
    else if (b > a) raw = 'to';
    else raw = 'tie';
  }
  if (raw !== 'tie') return { winner: raw, tiebreaker: null };
  if (fromStat != null && toStat != null) {
    if (fromStat > toStat) return { winner: 'from', tiebreaker: 'stat' };
    if (toStat > fromStat) return { winner: 'to',   tiebreaker: 'stat' };
  }
  return { winner: 'tie', tiebreaker: null };
}

// Backwards-compatible thin wrapper kept for callers that just want the
// winner string and don't care about the tiebreaker reason.
export function computeWinner(gameId, fromScore, toScore, fromStat, toStat) {
  return resolveOutcome(gameId, fromScore, toScore, fromStat, toStat).winner;
}

// Extract a simple display number from any score shape. Used by the default
// scoreboard. Games can override their own result UI via a `ResultDisplay`
// component on the module.
export function scoreNumber(gameId, score) {
  const game = getGame(gameId);
  if (game?.scoreNumber) return game.scoreNumber(score);
  if (score == null) return null;
  if (typeof score === 'number') return score;
  if (typeof score === 'object' && 'value' in score) return score.value;
  return score;
}
